import React, { useCallback, useRef, useState } from 'react';
import { useDismissOnOutsideClick } from '../../../../hooks/useDismissOnOutsideClick';
import { DragDropProvider } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { DateTimeFormatHelp } from '../../../../components/DateTimeFormatHelp';
import { FormGroup } from '../../../../components/FormGroup';
import { SpinnerInput } from '../../../../components/SpinnerInput';
import { ToggleSwitch } from '../../../../components/ToggleSwitch';
import type { LoadFileField, LoadFileFormat } from '../../../../types/export';
import { FieldRow } from './FieldRow';

interface Props {
  editFormat: LoadFileFormat;
  onChange: (format: LoadFileFormat) => void;
  loadFileFormats: LoadFileFormat[];
  saving: boolean;
}

function charPreview(code: number): string {
  return Number.isFinite(code) && code >= 0 && code <= 255 ? String.fromCharCode(code) : '';
}

interface CharCodeRowProps {
  label: string;
  id: string;
  value: number;
  onChange: (v: number) => void;
  disabled: boolean;
  extraContent?: React.ReactNode;
}

function CharCodeRow({ label, id, value, onChange, disabled, extraContent }: CharCodeRowProps) {
  return (
    <FormGroup label={label} htmlFor={id} labelCols="col-sm-4 text-right" inputCols="col-sm-8">
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <SpinnerInput
          id={id}
          value={value}
          onChange={onChange}
          min={0}
          max={255}
          disabled={disabled}
          required
          style={{ width: 80 }}
        />
        <input
          type="text"
          className="form-control uneditable-input"
          style={{ width: 50 }}
          value={charPreview(value)}
          readOnly
          disabled
        />
        {extraContent}
      </div>
    </FormGroup>
  );
}

export function Step2_1LoadFile({ editFormat, onChange, loadFileFormats, saving }: Props) {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showDateHelp, setShowDateHelp] = useState(false);
  const [showNewFieldMenu, setShowNewFieldMenu] = useState(false);
  const newFieldMenuRef = useRef<HTMLDivElement>(null);
  useDismissOnOutsideClick(newFieldMenuRef, showNewFieldMenu, () => setShowNewFieldMenu(false));

  const set = useCallback(
    <K extends keyof LoadFileFormat>(key: K, value: LoadFileFormat[K]) => {
      onChange({ ...editFormat, [key]: value });
    },
    [editFormat, onChange],
  );

  const touch = (name: string) => setTouched(prev => ({ ...prev, [name]: true }));

  const handleTemplateChange = (templateId: string) => {
    const tpl = loadFileFormats.find(f => f.id === templateId);
    if (!tpl) {
      onChange({ ...editFormat, template: templateId });
      return;
    }
    const { id: _id, name: _name, ...rest } = tpl;
    onChange({ ...editFormat, template: templateId, ...rest, name: editFormat.name });
  };

  const setField = (index: number, updated: LoadFileField) => {
    const fields = editFormat.fields.map((f, i) => (i === index ? updated : f));
    set('fields', fields);
  };

  const removeField = (index: number) => {
    set('fields', editFormat.fields.filter((_, i) => i !== index));
  };

  const addCustomField = () => {
    set('fields', [
      { type: 'custom', name: '', outputName: '', isEnabled: true, parts: [] },
      ...editFormat.fields,
    ]);
  };

  const addTagField = () => {
    set('fields', [
      { type: 'tag', name: '', outputName: '', isEnabled: true, parts: [] },
      ...editFormat.fields,
    ]);
  };

  const toggleAll = (enabled: boolean) => {
    set('fields', editFormat.fields.map(f => ({ ...f, isEnabled: enabled })));
  };

  const allEnabled = editFormat.fields.length > 0 && editFormat.fields.every(f => f.isEnabled);

  const nameError =
    touched.name && !editFormat.name ? 'Name is required' :
    touched.name && editFormat.name.length > 255 ? 'Name must be less than 255 characters' : undefined;

  const extError =
    touched.fileExtension && !editFormat.fileExtension ? 'File Extension is required' :
    touched.fileExtension && editFormat.fileExtension.length > 255 ? 'File Extension must be less than 255 characters' : undefined;

  return (
    <div style={{ overflowX: 'hidden' }}>
        {/* Format name */}
        <FormGroup label="Format name" htmlFor="lfName" error={nameError} labelCols="col-sm-4 text-right" inputCols="col-sm-8">
          <input
            type="text"
            id="lfName"
            className="form-control"
            value={editFormat.name}
            maxLength={255}
            required
            disabled={saving}
            onChange={e => set('name', e.target.value)}
            onBlur={() => touch('name')}
          />
        </FormGroup>

        {/* File extension */}
        <FormGroup label="File extension" htmlFor="lfExt" error={extError} labelCols="col-sm-4 text-right" inputCols="col-sm-3">
          <input
            type="text"
            id="lfExt"
            className="form-control"
            value={editFormat.fileExtension}
            maxLength={255}
            required
            disabled={saving}
            onChange={e => set('fileExtension', e.target.value)}
            onBlur={() => touch('fileExtension')}
          />
        </FormGroup>

        {/* Starter template */}
        <FormGroup label="Starter template" htmlFor="lfTemplate" labelCols="col-sm-4 text-right" inputCols="col-sm-5">
          <select
            id="lfTemplate"
            className="form-control"
            value={editFormat.template ?? ''}
            onChange={e => handleTemplateChange(e.target.value)}
            disabled={saving}
          >
            <option value="">-- choose starter template --</option>
            {loadFileFormats.map(f => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </FormGroup>

        <legend>Delimiter Character Codes</legend>

        <CharCodeRow
          label="Column"
          id="colDelim"
          value={editFormat.columnDelimiterCharCode}
          onChange={v => set('columnDelimiterCharCode', v)}
          disabled={saving}
        />
        <CharCodeRow
          label="Quote"
          id="quoteChar"
          value={editFormat.quoteCharCode}
          onChange={v => set('quoteCharCode', v)}
          disabled={saving}
        />
        <CharCodeRow
          label="Multi Value"
          id="multiValChar"
          value={editFormat.multiValueCharCode}
          onChange={v => set('multiValueCharCode', v)}
          disabled={saving}
          extraContent={
            <div className="checkbox checkbox-primary" style={{ margin: 0 }}>
              <input
                id="includeMultiVal"
                type="checkbox"
                checked={editFormat.includeMultiValueFieldConfig}
                disabled={saving}
                onChange={e => set('includeMultiValueFieldConfig', e.target.checked)}
              />
              <label htmlFor="includeMultiVal">Include Configuration File</label>
            </div>
          }
        />
        <CharCodeRow
          label="Nested Values"
          id="nestedValChar"
          value={editFormat.nestedValuesCharCode}
          onChange={v => set('nestedValuesCharCode', v)}
          disabled={saving}
        />
        <CharCodeRow
          label="Embedded Newline"
          id="embNewlineChar"
          value={editFormat.embeddedNewlineCharCode}
          onChange={v => set('embeddedNewlineCharCode', v)}
          disabled={saving}
        />

        {/* Newline */}
        <FormGroup label="Newline" htmlFor="lfNewline" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
          <div className="btn-group">
            <button
              type="button"
              className={`btn btn-default${editFormat.newline.type === 'unix' ? ' active' : ''}`}
              disabled={saving}
              onClick={() => set('newline', { type: 'unix' })}
            >
              Unix
            </button>
            <button
              type="button"
              className={`btn btn-default${editFormat.newline.type === 'windows' ? ' active' : ''}`}
              disabled={saving}
              onClick={() => set('newline', { type: 'windows' })}
            >
              Windows
            </button>
          </div>
        </FormGroup>

        {/* Date/Time Format */}
        <legend>
          Date/Time Format{' '}
          <button
            type="button"
            className="btn btn-default btn-xs"
            onClick={() => setShowDateHelp(v => !v)}
          >
            <i className="fas fa-question-circle" title="Show definitions" />
          </button>
        </legend>

        {showDateHelp && <DateTimeFormatHelp />}

        <FormGroup label="Date Format" htmlFor="lfDateFmt" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
          <input
            type="text"
            id="lfDateFmt"
            className="form-control"
            value={editFormat.dateFormat}
            maxLength={255}
            required
            disabled={saving}
            onChange={e => set('dateFormat', e.target.value)}
          />
        </FormGroup>
        <FormGroup label="Time Format" htmlFor="lfTimeFmt" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
          <input
            type="text"
            id="lfTimeFmt"
            className="form-control"
            value={editFormat.timeFormat}
            maxLength={255}
            required
            disabled={saving}
            onChange={e => set('timeFormat', e.target.value)}
          />
        </FormGroup>
        <FormGroup label="Date Time Format" htmlFor="lfDateTimeFmt" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
          <input
            type="text"
            id="lfDateTimeFmt"
            className="form-control"
            value={editFormat.dateTimeFormat}
            maxLength={255}
            required
            disabled={saving}
            onChange={e => set('dateTimeFormat', e.target.value)}
          />
        </FormGroup>

        <legend>File Paths</legend>

        <FormGroup label="Prepend Path" htmlFor="lfPrependPath" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
          <input
            type="text"
            id="lfPrependPath"
            className="form-control"
            value={editFormat.prependPath ?? ''}
            maxLength={255}
            disabled={saving}
            onChange={e => set('prependPath', e.target.value || null)}
          />
        </FormGroup>
        <FormGroup label="Path Separator" htmlFor="lfPathSep" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
          <input
            type="text"
            id="lfPathSep"
            className="form-control"
            value={editFormat.pathSeparator}
            maxLength={255}
            required
            disabled={saving}
            onChange={e => set('pathSeparator', e.target.value)}
          />
        </FormGroup>

        <div style={{ paddingBottom: 20 }} />

        {/* Export Field Settings */}
        <legend style={{ width: '100%' }}>
          Export Field Settings
          <div ref={newFieldMenuRef} className="btn-group btn-group-sm pull-right" style={{ marginTop: -7, position: 'relative' }}>
            <button
              type="button"
              className="btn btn-small btn-primary dropdown-toggle"
              onClick={() => setShowNewFieldMenu(v => !v)}
            >
              New Field <span className="caret" />
            </button>
            {showNewFieldMenu && (
              <ul className="dropdown-menu dropdown-menu-right" style={{ display: 'block' }}>
                <li><a href="#" onClick={e => { e.preventDefault(); addCustomField(); setShowNewFieldMenu(false); }}>Custom Field</a></li>
                <li><a href="#" onClick={e => { e.preventDefault(); addTagField(); setShowNewFieldMenu(false); }}>Tag Field</a></li>
              </ul>
            )}
          </div>
        </legend>

        <div>
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: 24 }} />
                <th style={{ width: '40%' }}>
                  Header
                  <span className="pull-right">
                    <ToggleSwitch
                      checked={editFormat.hasHeaderRow}
                      onChange={v => set('hasHeaderRow', v)}
                      onLabel="Show"
                      offLabel="Hide"
                      size="small"
                    />
                  </span>
                </th>
                <th>Value</th>
                <th style={{ whiteSpace: 'nowrap', width: 1 }}>
                  <ToggleSwitch
                    checked={allEnabled}
                    onChange={toggleAll}
                    onLabel="ON"
                    offLabel="OFF"
                    size="small"
                  />
                </th>
              </tr>
            </thead>
            <DragDropProvider
              onDragEnd={({ canceled, operation }) => {
                if (canceled || !isSortable(operation.source)) return;
                const { initialIndex, index } = operation.source.sortable;
                if (initialIndex === index) return;
                const next = [...editFormat.fields];
                const [moved] = next.splice(initialIndex, 1);
                next.splice(index, 0, moved);
                onChange({ ...editFormat, fields: next });
              }}
            >
              <tbody>
                {editFormat.fields.map((field, i) => (
                  <FieldRow
                    key={field.name || `${field.type}-${i}`}
                    field={field}
                    index={i}
                    saving={saving}
                    onChange={updated => setField(i, updated)}
                    onRemove={() => removeField(i)}
                  />
                ))}
                {editFormat.fields.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted">No fields. Add a Custom or Tag field above.</td>
                  </tr>
                )}
              </tbody>
            </DragDropProvider>
          </table>
        </div>
    </div>
  );
}
